import React, { createContext, FC, ReactNode, useContext } from 'react';

import {
	type PaymentMethodsBannerState,
	usePaymentMethodsBanner,
} from '~hooks/billing/usePaymentMethodsBanner';

/**
 * Whether the global suspension banner is on screen, and why.
 *
 * Resolved once at the app root and read from context rather than recomputed per consumer,
 * because the consumers are not all app-level components: `Popout` and the location dropdown are
 * generic commons rendered in dozens of places, and they only need to offset themselves under the
 * banner. Making them derive this themselves would couple every one of them to the session store
 * and to LaunchDarkly.
 *
 * The default is "no banner", so a component rendered without the provider — a commons component
 * under test, most often — keeps working and simply draws no offset.
 */
const DEFAULT_STATE: PaymentMethodsBannerState = {
	isPaymentMethodsBannerEnabled: false,
	isSuspensionBannerVisible: false,
};

const SuspensionBannerContext = createContext<PaymentMethodsBannerState>(DEFAULT_STATE);

export const SuspensionBannerProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const state = usePaymentMethodsBanner();

	return (
		<SuspensionBannerContext.Provider value={state}>
			{children}
		</SuspensionBannerContext.Provider>
	);
};

export const useSuspensionBanner = (): PaymentMethodsBannerState =>
	useContext(SuspensionBannerContext);
